export type BillingFrequencyType = "days" | "months";

export interface PlanBillingFrequency {
  billing_frequency_type: BillingFrequencyType;
  billing_frequency: number;
}

export interface PlanBillingFrequencyOption extends PlanBillingFrequency {
  key: "daily" | "weekly" | "monthly" | "yearly";
  label: string;
  available: boolean;
  grace_days: number | null;
  advance_notice_days: number | null;
  price_suffix: string;
}

export const MONTHLY_GRACE_DAYS = 5;
export const MIN_GRACE_DAYS = 0;
export const MAX_GRACE_DAYS = 60;

export const PLAN_BILLING_FREQUENCY_OPTIONS: readonly PlanBillingFrequencyOption[] = [
  { key: "daily", label: "Diario", billing_frequency_type: "days", billing_frequency: 1, available: true, grace_days: null, advance_notice_days: null, price_suffix: "/día" },
  { key: "weekly", label: "Semanal", billing_frequency_type: "days", billing_frequency: 7, available: true, grace_days: 2, advance_notice_days: null, price_suffix: "/semana" },
  { key: "monthly", label: "Mensual", billing_frequency_type: "months", billing_frequency: 1, available: true, grace_days: MONTHLY_GRACE_DAYS, advance_notice_days: 3, price_suffix: "/mes" },
  { key: "yearly", label: "Anual", billing_frequency_type: "months", billing_frequency: 12, available: true, grace_days: 14, advance_notice_days: 7, price_suffix: "/año" },
];

export const DEFAULT_PLAN_BILLING_FREQUENCY: PlanBillingFrequency = {
  billing_frequency_type: "months",
  billing_frequency: 1,
};

const MIN_FREQUENCY = 1;
const MAX_FREQUENCY = 365;

export function planBillingFrequency(plan: {
  billing_frequency_type?: string | null;
  billing_frequency?: number | string | null;
} | null | undefined): PlanBillingFrequency | { error: string } {
  const type = plan?.billing_frequency_type || DEFAULT_PLAN_BILLING_FREQUENCY.billing_frequency_type;
  if (type !== "days" && type !== "months") return { error: `invalid billing_frequency_type '${type}'` };
  const raw = plan?.billing_frequency;
  const frequency = raw === null || raw === undefined ? DEFAULT_PLAN_BILLING_FREQUENCY.billing_frequency : Number(raw);
  if (!Number.isInteger(frequency) || frequency < MIN_FREQUENCY || frequency > MAX_FREQUENCY) {
    return { error: `invalid billing_frequency '${raw}'` };
  }
  return { billing_frequency_type: type, billing_frequency: frequency };
}

export function planBillingFrequencyOption(plan: {
  billing_frequency_type?: string | null;
  billing_frequency?: number | string | null;
} | null | undefined): PlanBillingFrequencyOption | null {
  const resolved = planBillingFrequency(plan);
  if ("error" in resolved) return null;
  return PLAN_BILLING_FREQUENCY_OPTIONS.find((option) =>
    option.billing_frequency_type === resolved.billing_frequency_type &&
    option.billing_frequency === resolved.billing_frequency
  ) ?? null;
}

export type RequestedBillingFrequency =
  | { ok: true; value: PlanBillingFrequency | null }
  | { ok: false; error: string };

export function readRequestedBillingFrequency(body: Record<string, unknown> | null | undefined): RequestedBillingFrequency {
  const type = body?.billing_frequency_type;
  const frequency = body?.billing_frequency;
  if (type === undefined && frequency === undefined) return { ok: true, value: null };
  const option = planBillingFrequencyOption({
    billing_frequency_type: typeof type === "string" ? type : null,
    billing_frequency: typeof frequency === "number" || typeof frequency === "string" ? frequency : null,
  });
  if (!option || typeof type !== "string" || frequency === undefined || frequency === null) {
    return { ok: false, error: "La frecuencia de cobro no es válida." };
  }
  if (!option.available) {
    return { ok: false, error: `La frecuencia de cobro ${option.label.toLowerCase()} todavía no está disponible.` };
  }
  return {
    ok: true,
    value: { billing_frequency_type: option.billing_frequency_type, billing_frequency: option.billing_frequency },
  };
}

export const PER_USE_FREQUENCY_KEYS: readonly PlanBillingFrequencyOption["key"][] = ["weekly", "monthly"];

export function billingModeFrequencyError(
  billingMode: string | null | undefined,
  frequency: PlanBillingFrequency | null | undefined,
): string | null {
  if (billingMode !== "per_use" || !frequency) return null;
  const option = planBillingFrequencyOption(frequency);
  if (!option || !PER_USE_FREQUENCY_KEYS.includes(option.key)) {
    return "Un plan por consumo se cobra semanal o mensual.";
  }
  return null;
}

const PER_USE_CADENCE: Record<PlanBillingFrequencyOption["key"], string> = {
  daily: "diario",
  weekly: "semanal",
  monthly: "a mes vencido",
  yearly: "anual",
};

export function perUseChargeTerms(
  plan: {
    per_conversation_price?: number | string | null;
    minimum_charge?: number | string | null;
    billing_frequency_type?: string | null;
    billing_frequency?: number | string | null;
  } | null | undefined,
  formatMoney: (amount: number) => string,
): string | null {
  const price = Number(plan?.per_conversation_price ?? 0);
  if (!(price > 0)) return null;
  const option = planBillingFrequencyOption(plan);
  const parts = [`${formatMoney(price)} por conversación`];
  if (option) parts.push(PER_USE_CADENCE[option.key]);
  const minimum = Number(plan?.minimum_charge ?? 0);
  if (minimum > 0) parts.push(`mínimo ${formatMoney(minimum)}`);
  return parts.join(", ");
}

type PlanTerms = {
  billing_frequency_type?: string | null;
  billing_frequency?: number | string | null;
  grace_days?: number | string | null;
} | null | undefined;

function resolvedOption(plan: PlanTerms): PlanBillingFrequencyOption | null | "invalid" {
  const resolved = planBillingFrequency(plan);
  if ("error" in resolved) return "invalid";
  return planBillingFrequencyOption(resolved);
}

export function planInDunning(plan: PlanTerms): boolean {
  const option = resolvedOption(plan);
  if (option === "invalid") return true;
  return option !== null && option.grace_days !== null;
}

export function defaultGraceDays(plan: PlanTerms): number {
  const option = resolvedOption(plan);
  if (option === "invalid" || option === null) return MONTHLY_GRACE_DAYS;
  return option.grace_days ?? MONTHLY_GRACE_DAYS;
}

function validGraceDays(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= MIN_GRACE_DAYS && n <= MAX_GRACE_DAYS ? n : null;
}

export function planGraceDays(plan: PlanTerms): number {
  return validGraceDays(plan?.grace_days) ?? defaultGraceDays(plan);
}

export function planAdvanceNoticeDays(plan: PlanTerms): number | null {
  const option = resolvedOption(plan);
  if (option === "invalid") return PLAN_BILLING_FREQUENCY_OPTIONS.find((o) => o.key === "monthly")!.advance_notice_days;
  return option?.advance_notice_days ?? null;
}

export function advanceNoticeOffsets(): number[] {
  return [...new Set(
    PLAN_BILLING_FREQUENCY_OPTIONS
      .map((o) => o.advance_notice_days)
      .filter((d): d is number => d !== null),
  )].sort((a, b) => a - b);
}

export function planPriceSuffix(plan: PlanTerms): string {
  const option = resolvedOption(plan);
  if (option === "invalid" || option === null) return "/mes";
  return option.price_suffix;
}

export type RequestedGraceDays =
  | { ok: true; value: number | null | undefined }
  | { ok: false; error: string };

export function readRequestedGraceDays(body: Record<string, unknown> | null | undefined): RequestedGraceDays {
  if (!body || !("grace_days" in body) || body.grace_days === undefined) return { ok: true, value: undefined };
  const raw = body.grace_days;
  if (raw === null || raw === "") return { ok: true, value: null };
  const value = validGraceDays(raw);
  if (value === null) {
    return { ok: false, error: `Los días de gracia deben ser un número entero entre ${MIN_GRACE_DAYS} y ${MAX_GRACE_DAYS}.` };
  }
  return { ok: true, value };
}
